import { BusinessObjectBase } from './base';
import { sessionSchema } from '@schemas/business-objects/session';
import type { Attendance } from './attendance';
import type { Curriculum } from './curriculum';
import type { SessionActivityItem } from './session-activity-item';
import type { CalendarDate, Id } from '../base-types';

export interface SessionData {
  readonly id: Id;
  readonly date?: CalendarDate;
  readonly comments?: string;
  readonly sessionLengthMinutes?: number;
  readonly complete?: boolean;
  readonly sessionNumber?: number;
  readonly attendanceId: Id;
  readonly attendance?: Attendance;
  readonly curriculumId: Id;
  readonly curriculum?: Curriculum;
  readonly sessionActivityItems?: SessionActivityItem[];
}

export interface Session extends Readonly<SessionData> {}
export class Session extends BusinessObjectBase {
  static readonly entityName = 'Session' as const;
  static readonly plural = 'sessions';
  static schema = sessionSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Session[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Session(data));
  }

  static async findOne(filter?: any): Promise<Session | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Session(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Session> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Session(data);
  }

  static async create(data: Omit<SessionData, 'id'>): Promise<Session> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Session(result);
  }

  static async createMany(data: Omit<SessionData, 'id'>[]): Promise<Session[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Session(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<SessionData>,
  ): Promise<Session> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Session(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
